import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { UserIcon, TrashIcon, PencilIcon, ChevronDownIcon, ChevronRightIcon, SearchIcon } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export function UserTable({ 
  users,
  onCreateUser,
  onEditUser,
  onDeleteUser,
  onDeleteOldEntries,
  onToggleUserActivities,
  expandedUsers,
  userActivities
}) {
  // Local state for dialogs and forms
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  const [newUserForm, setNewUserForm] = useState({ 
    username: '', 
    password: '', 
    full_name: '', 
    role: 'user' 
  });
  
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    full_name: '',
    role: 'user',
    password: ''
  });

  // Filter users based on search term
  const getFilteredUsers = () => {
    if (!userSearchTerm.trim()) {
      return users;
    }
    const searchLower = userSearchTerm.toLowerCase().trim();
    return users.filter(user => 
      user.username?.toLowerCase().includes(searchLower) ||
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  };

  // Handle create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    const success = await onCreateUser(newUserForm);
    if (success) {
      setNewUserForm({ username: '', password: '', full_name: '', role: 'user' });
      setShowCreateUser(false);
    }
  };

  // Open edit user dialog
  const openEditUserDialog = (user) => {
    setSelectedUserForEdit(user);
    setEditUserForm({
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      password: ''
    });
    setShowEditUserDialog(true);
  };

  // Handle edit user submit
  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserForEdit) return;

    const updateData = {};
    if (editUserForm.username !== selectedUserForEdit.username) {
      updateData.username = editUserForm.username;
    }
    if (editUserForm.full_name !== selectedUserForEdit.full_name) {
      updateData.full_name = editUserForm.full_name;
    }
    if (editUserForm.role !== selectedUserForEdit.role) {
      updateData.role = editUserForm.role;
    }
    if (editUserForm.password && editUserForm.password.trim()) {
      updateData.password = editUserForm.password;
    }

    if (Object.keys(updateData).length === 0) {
      setShowEditUserDialog(false);
      return;
    }

    const success = await onEditUser(selectedUserForEdit.id, updateData, editUserForm.full_name);
    if (success) {
      setShowEditUserDialog(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Benutzer verwalten</h2>
        
        <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
          <DialogTrigger asChild>
            <Button>
              <UserIcon className="h-4 w-4 mr-2" />
              Neuer Benutzer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="new_username">Benutzername</Label>
                <Input
                  id="new_username"
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_password">Passwort</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="full_name">Vollständiger Name</Label>
                <Input
                  id="full_name"
                  value={newUserForm.full_name}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Rolle</Label>
                <Select value={newUserForm.role} onValueChange={(value) => setNewUserForm(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Benutzer</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full">Erstellen</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie die Benutzerdaten. Lassen Sie das Passwort-Feld leer, um das aktuelle Passwort beizubehalten.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUserSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_username">Benutzername</Label>
              <Input
                id="edit_username"
                value={editUserForm.username}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, username: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_full_name">Vollständiger Name</Label>
              <Input
                id="edit_full_name"
                value={editUserForm.full_name}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select 
                value={editUserForm.role} 
                onValueChange={(value) => setEditUserForm(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Benutzer</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_password">Neues Passwort (optional)</Label>
              <Input
                id="edit_password"
                type="password"
                value={editUserForm.password}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Leer lassen um Passwort nicht zu ändern"
              />
              <p className="text-sm text-gray-500 mt-1">
                Nur ausfüllen, wenn Sie das Passwort ändern möchten.
              </p>
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">Speichern</Button>
              <Button type="button" variant="outline" onClick={() => setShowEditUserDialog(false)}>
                Abbrechen
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search bar */}
      <div className="flex justify-center">
        <div className="relative max-w-2xl w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Benutzer suchen (Name, Benutzername, Rolle)..."
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
          {userSearchTerm && (
            <button
              onClick={() => setUserSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* User Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Benutzername</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Erstellt am</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getFilteredUsers().map((user) => (
                <React.Fragment key={user.id}>
                  <TableRow>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        {user.role === 'user' && (
                          <button
                            onClick={() => onToggleUserActivities(user.id)}
                            className="mr-2 p-1 hover:bg-gray-100 rounded"
                          >
                            {expandedUsers[user.id] ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {user.username}
                      </div>
                    </TableCell>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                      </Badge>
                    </TableCell>
                    <TableCell>{format(new Date(user.created_at), "PPP", { locale: de })}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditUserDialog(user)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Bearbeiten
                        </Button>
                        {user.role === 'user' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onDeleteOldEntries(user.id, user.full_name)}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            >
                              <TrashIcon className="h-4 w-4 mr-1" />
                              Alte Einträge
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onDeleteUser(user.id, user.full_name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4 mr-1" />
                              Benutzer löschen
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedUsers[user.id] && userActivities[user.id] && (
                    <TableRow>
                      <TableCell colSpan={5} className="bg-gray-50">
                        <div className="py-4">
                          <h4 className="font-medium mb-3 text-gray-700">
                            Arbeitsschritte der letzten 5 Tage von {user.full_name}:
                          </h4>
                          {userActivities[user.id].length > 0 ? (
                            <div className="space-y-3">
                              {userActivities[user.id].map((activity, index) => (
                                <div key={index} className="bg-white p-4 rounded border">
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <h5 className="font-medium text-gray-800">{activity.task.title}</h5>
                                      <p className="text-sm text-gray-600">{activity.task.work_instruction}</p>
                                    </div>
                                    <Badge className="bg-green-100 text-green-800">
                                      {activity.work_step.step_type}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <span className="font-medium text-gray-600">Abgeschlossen:</span>
                                      <br />
                                      {format(new Date(activity.assignment.completed_at), "PPP p", { locale: de })}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-600">Gesamtzeit:</span>
                                      <br />
                                      <span className="font-semibold text-blue-600">
                                        {activity.total_time_minutes} Min
                                      </span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-600">Sitzungen:</span>
                                      <br />
                                      {activity.session_count} Arbeitssitzung{activity.session_count !== 1 ? 'en' : ''}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-600">Artikelnummer:</span>
                                      <br />
                                      {activity.task.article_number || '-'}
                                    </div>
                                  </div>
                                  {activity.work_step.notes && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      Arbeitsschritt-Notizen: {activity.work_step.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">Keine abgeschlossenen Arbeitsschritte in den letzten 5 Tagen</p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default UserTable;
